/**
 * IntegrityCheckService - Startup corruption detection for workspace documents
 *
 * Part of sl-canvas2-8.24.21: Automatische Corruption Detection beim Startup
 *
 * SOLID: Single Responsibility - only checks integrity, delegates reporting
 * DRY: Uses existing safeApplyUpdate validation logic via encodeStateVectorFromUpdate
 */

import { DebugLogger } from '@affine/debug';
import { type DocRecord, isQuotaExceededError } from '@affine/nbstore';
import type { StoreClient } from '@affine/nbstore/worker/client';
import { encodeStateVectorFromUpdate } from 'yjs';

import type { CorruptionReporterService } from '../../cloud';

// Dev-only diagnostics. Off by default; enable via `?debug` (DebugLogger /
// @affine/debug). Prod builds strip console.* via webpack pure_funcs anyway.
const logger = new DebugLogger('affine:workspace:integrity');

// Type extension for StoreClient methods added in sl-canvas2-8.24.21
// These methods exist at runtime but types are not yet exported
interface StoreClientWithIntegrity extends StoreClient {
  getDoc(docId: string): Promise<DocRecord | null>;
  getDocTimestamps(after?: Date): Promise<Record<string, Date>>;
}

export interface IntegrityCheckResult {
  docId: string;
  status: 'ok' | 'corrupt' | 'empty' | 'storage-full';
  size?: number;
  error?: string;
}

export interface IntegrityCheckSummary {
  total: number;
  ok: number;
  corrupt: number;
  empty: number;
  storageFull: number;
  corruptDocs: IntegrityCheckResult[];
  storageFullDocs: IntegrityCheckResult[];
  durationMs: number;
}

export interface IntegrityCheckOptions {
  /** Max docs to check. 0 = all. Default: 0 */
  limit?: number;
  /** Delay between docs in ms (throttling). Default: 0 */
  delayMs?: number;
  /** Sample mode - only check random sample. Default: false */
  sampleMode?: boolean;
  /** Sample size if sampleMode is true. Default: 5 */
  sampleSize?: number;
  /** Callback for progress */
  onProgress?: (checked: number, total: number) => void;
  /** Callback when corrupt doc found */
  onCorruptDoc?: (result: IntegrityCheckResult) => void;
  /** AbortSignal */
  signal?: AbortSignal;
}

/**
 * Service to check workspace document integrity at startup.
 *
 * Usage:
 * ```typescript
 * const service = new IntegrityCheckService(storeClient, corruptionReporter, workspaceId);
 * const summary = await service.checkAll();
 * if (summary.corrupt > 0) {
 *   // Show warning toast
 * }
 * ```
 */
export class IntegrityCheckService {
  private readonly client: StoreClientWithIntegrity;

  constructor(
    client: StoreClient,
    private readonly corruptionReporter: CorruptionReporterService | undefined,
    private readonly workspaceId: string
  ) {
    // Cast to extended type - methods exist at runtime (sl-canvas2-8.24.21)
    this.client = client as StoreClientWithIntegrity;
  }

  /**
   * Check a single document
   */
  async checkDoc(docId: string): Promise<IntegrityCheckResult> {
    try {
      const doc = await this.client.getDoc(docId);

      if (!doc || !doc.bin || doc.bin.length === 0) {
        return { docId, status: 'empty' };
      }

      // Quick validation using encodeStateVectorFromUpdate
      try {
        encodeStateVectorFromUpdate(doc.bin);
        return { docId, status: 'ok', size: doc.bin.length };
      } catch (err) {
        const error = err instanceof Error ? err.message : String(err);
        console.error(`[IntegrityCheck] Corrupt doc: ${docId}`, error);

        // Report to corruption reporter
        this.corruptionReporter?.report({
          workspaceId: this.workspaceId,
          docId,
          errorType: 'INTEGRITY_CHECK_FAILED',
          errorMessage: error,
          operation: 'startup_check',
          binLength: doc.bin.length,
          binPreview: doc.bin.slice(0, 100),
        });

        return { docId, status: 'corrupt', size: doc.bin.length, error };
      }
    } catch (err) {
      const error = err instanceof Error ? err.message : String(err);
      if (isQuotaExceededError(err)) {
        return {
          docId,
          status: 'storage-full',
          error: `Local storage quota exceeded: ${error}`,
        };
      }
      return { docId, status: 'corrupt', error: `Read error: ${error}` };
    }
  }

  /**
   * Check all documents in workspace
   */
  async checkAll(
    options: IntegrityCheckOptions = {}
  ): Promise<IntegrityCheckSummary> {
    const {
      limit = 0,
      delayMs = 0,
      sampleMode = false,
      sampleSize = 5,
      onProgress,
      onCorruptDoc,
      signal,
    } = options;

    const startTime = Date.now();
    const results: IntegrityCheckResult[] = [];
    const corruptDocs: IntegrityCheckResult[] = [];
    const storageFullDocs: IntegrityCheckResult[] = [];

    await this.client.waitForLocalDocStorageConnected();

    // Get all doc timestamps
    const timestamps = await this.client.getDocTimestamps();
    let docIds = Object.keys(timestamps ?? {});

    if (docIds.length === 0) {
      return {
        total: 0,
        ok: 0,
        corrupt: 0,
        empty: 0,
        storageFull: 0,
        corruptDocs: [],
        storageFullDocs: [],
        durationMs: Date.now() - startTime,
      };
    }

    // Sample mode: random sample
    if (sampleMode) {
      docIds = docIds
        .sort(() => Math.random() - 0.5)
        .slice(0, Math.min(sampleSize, docIds.length));
    }

    // Limit mode
    if (limit > 0) {
      docIds = docIds.slice(0, limit);
    }

    // Check each doc
    for (let i = 0; i < docIds.length; i++) {
      if (signal?.aborted) break;

      const result = await this.checkDoc(docIds[i]);
      results.push(result);

      if (result.status === 'corrupt') {
        corruptDocs.push(result);
        onCorruptDoc?.(result);
      } else if (result.status === 'storage-full') {
        storageFullDocs.push(result);
      }

      onProgress?.(i + 1, docIds.length);

      // Throttle
      if (delayMs > 0 && i < docIds.length - 1) {
        await new Promise(r => setTimeout(r, delayMs));
      }
    }

    const summary: IntegrityCheckSummary = {
      total: results.length,
      ok: results.filter(r => r.status === 'ok').length,
      corrupt: corruptDocs.length,
      empty: results.filter(r => r.status === 'empty').length,
      storageFull: storageFullDocs.length,
      corruptDocs,
      storageFullDocs,
      durationMs: Date.now() - startTime,
    };

    if (summary.corrupt > 0) {
      console.warn(
        `[IntegrityCheck] Found ${summary.corrupt} corrupt docs out of ${summary.total}`,
        corruptDocs.map(d => d.docId)
      );
    } else if (summary.storageFull > 0) {
      console.warn(
        `[IntegrityCheck] Local storage quota exceeded while checking ${summary.storageFull} docs out of ${summary.total}`,
        storageFullDocs.map(d => d.docId)
      );
    } else {
      logger.debug(
        `[IntegrityCheck] All ${summary.total} docs OK (${summary.durationMs}ms)`
      );
    }

    return summary;
  }

  /**
   * Quick check - sample of docs for fast startup
   */
  async quickCheck(sampleSize = 5): Promise<IntegrityCheckSummary> {
    return this.checkAll({ sampleMode: true, sampleSize });
  }
}
